// pages/public/MenuPage.jsx
import React, { useEffect, useState } from 'react';
import PublicLayout from '../../layout/PublicLayout';
import ProductCard from '../../components/public/ProductCard';
import { getProductosPublicos, getCategoriasPublicas, getRestauranteActual } from '../../api/public';
import { useTypewriterColored } from '../../hooks/useTypewriterColored';
import { Search, Filter, X } from 'lucide-react';

const MenuPage = () => {
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('all');
  const [sortBy, setSortBy] = useState('nombre');
  const [showFilters, setShowFilters] = useState(false);

  // Estado para detectar si el navbar principal está visible
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Efecto typewriter para "Nuestro Menú"
  // "Nuestro " tiene 8 caracteres (incluyendo el espacio)
  const { part1, part2 } = useTypewriterColored('Nuestro Menú', 8, 100, 50, 2000);

  // Detectar scroll para ajustar posición del navbar de búsqueda
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        setIsNavbarVisible(true);
      } else if (currentScrollY > lastScrollY) {
        setIsNavbarVisible(false);
      } else {
        setIsNavbarVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Cargar productos, categorías y datos del restaurante
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Cargar productos (ya vienen filtrados como activos desde el backend)
        const productosData = await getProductosPublicos();
        setProductos(productosData);
        setFilteredProductos(productosData);

        // Cargar categorías (ya vienen filtradas como activas desde el backend)
        const categoriasData = await getCategoriasPublicas();
        setCategorias(categoriasData);

        // Cargar datos del restaurante para obtener el WhatsApp
        const restauranteData = await getRestauranteActual();
        if (restauranteData && restauranteData.telefono) {
          setWhatsappNumber(restauranteData.telefono);
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let result = [...productos];

    // Filtrar por búsqueda
    if (searchTerm) {
      result = result.filter(p =>
        p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por categoría
    if (selectedCategoria !== 'all') {
      result = result.filter(p => p.id_categoria === parseInt(selectedCategoria));
    }

    // Ordenar
    switch (sortBy) {
      case 'nombre':
        result.sort((a, b) => a.nombre.localeCompare(b.nombre));
        break;
      case 'precio-asc':
        result.sort((a, b) => (a.precio || 0) - (b.precio || 0));
        break;
      case 'precio-desc':
        result.sort((a, b) => (b.precio || 0) - (a.precio || 0));
        break;
      default:
        break;
    }

    setFilteredProductos(result);
  }, [searchTerm, selectedCategoria, sortBy, productos]);

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategoria('all');
    setSortBy('nombre');
  };

  // Contar productos por categoría
  const getProductCountByCategory = (categoriaId) => {
    if (categoriaId === 'all') return productos.length;
    return productos.filter(p => p.id_categoria === categoriaId).length;
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">{part1}</span>
              <span className="text-yellow-300">
                {part2}
                <span className="animate-pulse">|</span>
              </span>
            </h1>
            <p className="text-xl text-red-50 max-w-2xl mx-auto">
              Descubre nuestra deliciosa variedad de platos preparados con ingredientes frescos
            </p>
          </div>
        </div>
      </section>

      {/* Filtros y Búsqueda */}
      <section className={`bg-white shadow-md sticky z-40 transition-all duration-300 ${
        isNavbarVisible ? 'top-16' : 'top-0'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Barra de búsqueda */}
            <div className="relative flex-1 w-full md:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Botón de filtros móvil */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden bg-red-600 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 w-full justify-center"
            >
              <Filter className="w-5 h-5" />
              Filtros
            </button>

            {/* Ordenar - Desktop */}
            <div className="hidden md:flex items-center gap-4">
              <label className="text-gray-700 font-medium whitespace-nowrap">
                Ordenar por:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="nombre">Nombre (A-Z)</option>
                <option value="precio-asc">Precio (Menor a Mayor)</option>
                <option value="precio-desc">Precio (Mayor a Menor)</option>
              </select>
            </div>

            {/* Limpiar filtros */}
            {(searchTerm || selectedCategoria !== 'all' || sortBy !== 'nombre') && (
              <button
                onClick={clearFilters}
                className="hidden md:flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
              >
                <X className="w-4 h-4" />
                Limpiar
              </button>
            )}
          </div>

          {/* Filtros móvil desplegable */}
          {showFilters && (
            <div className="md:hidden mt-4 pt-4 border-t">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Ordenar por:
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="nombre">Nombre (A-Z)</option>
                    <option value="precio-asc">Precio (Menor a Mayor)</option>
                    <option value="precio-desc">Precio (Mayor a Menor)</option>
                  </select>
                </div>
                {(searchTerm || selectedCategoria !== 'all' || sortBy !== 'nombre') && (
                  <button
                    onClick={clearFilters}
                    className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300"
                  >
                    Limpiar Filtros
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contenido Principal */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar de Categorías - Desktop */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className={`bg-white rounded-2xl shadow-md p-6 sticky transition-all duration-300 ${
                isNavbarVisible ? 'top-32' : 'top-16'
              }`}>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Filter className="w-5 h-5 text-red-600" />
                  Categorías
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategoria('all')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategoria === 'all'
                        ? 'bg-red-600 text-white font-semibold'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>Todas</span>
                    <span className={`text-sm ${
                      selectedCategoria === 'all' ? 'text-red-100' : 'text-gray-500'
                    }`}>
                      ({getProductCountByCategory('all')})
                    </span>
                  </button>
                  {categorias.map((categoria) => (
                    <button
                      key={categoria.id_categoria}
                      onClick={() => setSelectedCategoria(categoria.id_categoria.toString())}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between ${
                        selectedCategoria === categoria.id_categoria.toString()
                          ? 'bg-red-600 text-white font-semibold'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span>{categoria.nombre}</span>
                      <span className={`text-sm ${
                        selectedCategoria === categoria.id_categoria.toString()
                          ? 'text-red-100'
                          : 'text-gray-500'
                      }`}>
                        ({getProductCountByCategory(categoria.id_categoria)})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Categorías - Móvil (Horizontal Scroll) */}
            <div className="lg:hidden overflow-x-auto pb-4 -mx-4 px-4">
              <div className="flex gap-3 min-w-max">
                <button
                  onClick={() => setSelectedCategoria('all')}
                  className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
                    selectedCategoria === 'all'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-red-600'
                  }`}
                >
                  Todas ({getProductCountByCategory('all')})
                </button>
                {categorias.map((categoria) => (
                  <button
                    key={categoria.id_categoria}
                    onClick={() => setSelectedCategoria(categoria.id_categoria.toString())}
                    className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
                      selectedCategoria === categoria.id_categoria.toString()
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-red-600'
                    }`}
                  >
                    {categoria.nombre} ({getProductCountByCategory(categoria.id_categoria)})
                  </button>
                ))}
              </div>
            </div>

            {/* Grid de Productos */}
            <div className="flex-1">
              {loading ? (
                <div className="text-center py-20">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-red-600"></div>
                  <p className="mt-4 text-gray-600 text-lg">Cargando menú...</p>
                </div>
              ) : filteredProductos.length > 0 ? (
                <>
                  {/* Contador de resultados */}
                  <div className="mb-6">
                    <p className="text-gray-600">
                      Mostrando <span className="font-semibold text-gray-900">{filteredProductos.length}</span> {filteredProductos.length === 1 ? 'producto' : 'productos'}
                      {selectedCategoria !== 'all' && (
                        <span> en <span className="font-semibold text-red-600">
                          {categorias.find(c => c.id_categoria.toString() === selectedCategoria)?.nombre}
                        </span></span>
                      )}
                    </p>
                  </div>

                  {/* Grid de productos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredProductos.map((producto) => (
                      <ProductCard 
                        key={producto.id_producto} 
                        producto={producto} 
                        whatsappNumber={whatsappNumber}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    No se encontraron productos
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Intenta con otros términos de búsqueda o categorías
                  </p>
                  <button
                    onClick={clearFilters}
                    className="bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors"
                  >
                    Ver todos los productos
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default MenuPage;
